import { Indicator, BonusOrReduction } from './price-to-fix.interface';
import { getDateEndPeriod, getDateTomorrow } from '@shared-logic/utils';
import { Price } from './price.interface';

/**
 * Given an indicator, returns true if it has a bonus or reduction.
 * @param {BonusOrReduction} bonusOrReduction - The bonus or reduction to be checked.
 * @returns {boolean} True if the indicator has a bonus or reduction, false otherwise.
 */
export function haveBonusOrReduction(bonusOrReduction: BonusOrReduction) {
    return bonusOrReduction.value > 0 && bonusOrReduction.type.length > 0
}

/**
 * First it will be determined if it has proportionality.
 * If it has, it will continue. If not, you can return an array with the same indicator passed by parameter.
 * 
 * Once we see if it has proportionality, we must look at the propQuantDivs field. That number will tell us 
 * how many elements the array that we will return will have.
 * The idea of ​​the function is to create different indicators where the start and end date of the fixation period varies 
 * depending on a certain criterion of the fields referring to proportionality.
 * 
 * The fixing period must be divided depending on the propPeriod field.
 * Thus the indicators will have the same information but with different fixation periods to be divided proportionally.
 * 
 * The first indicator will have the same start date. To calculate the end date we will use another function 
 * (which has not been created yet but I will detail it below).
 * Once we have the end date, and everything is assigned to an indicator, we can insert it into the arrangement. 
 * 
 * @param {Indicator} indicator - The indicator to be divided.
 * @returns {Indicator[]} - The array of indicators.
 * */
export function generateIndicators(indicator: Indicator): Indicator[] {
    const indicators: Indicator[] = [];

    if (!indicator.proporcionality) {
        return [indicator];
    }
    const propPeriod = indicator.propPeriod;
    const propQuantDivs = indicator.propQuantDivs;

    for (let i = 0; i < propQuantDivs; i++) {
        const lastIndicator = indicators[indicators.length - 1];
        const startDate = lastIndicator ? getDateTomorrow(lastIndicator.fixationPeriod.endDate) : indicator.fixationPeriod.startDate;
        const endDate = getDateEndPeriod(startDate, propPeriod);

        let newIndicator: Indicator = JSON.parse(JSON.stringify(indicator))
        newIndicator.fixationPeriod.endDate = endDate;
        newIndicator.fixationPeriod.startDate = startDate;
        indicators.push(newIndicator);
    }

    return indicators;
}

/**
 * Given an array of indicators, loop through each one.
 * If it has proportionality add each element of what generateIndicators() returns
 * If it does not have proportionality, add the same indicator.
 * @param {Indicator[]} indicators - The array of indicators to be divided.
 * @returns {Indicator[]} - The array of indicators.
 * */
export function getIndicatorsWithProporcionality(indicators: Indicator[]): Indicator[] {
    const newIndicators: Indicator[] = [];
    indicators.forEach((indicator: Indicator) => {
        if (indicator.proporcionality) {
            newIndicators.push(...generateIndicators(indicator))
        } else {
            newIndicators.push(indicator)
        }
    })
    return newIndicators;
}

/**
 * Resets the transmission state of all indicators in a price.
 * Each contract in IBM has an independent lifecycle, so when a new contract
 * is created (e.g., copy of an order, triple-band exchange) inheriting indicators
 * from a parent, they must be re-sent. This function marks all indicators as
 * pending transmission.
 * @param {Price} price - The price containing the indicators to reset.
 */
export function resetIndicatorsTransmission(price: Price) {
    if (price?.indicators?.length > 0) {
        for (const indicator of price.indicators) {
            indicator.wasSent = false;
            indicator.wasSentAt = null;
        }
    }
}
